/**
 * Challenge card header – countdown segments, coin pot, and outcome chips.
 * Composed by challengeCardHeaderLayout.styles.ts.
 */

import { Platform } from 'react-native';
import { makeBoxShadow } from '../../shared/helpers/shadowStyles.helper';
import type { Theme } from '../../theme/getTheme';

export function getChallengeCardHeaderCountdownStyles(theme: Theme, narrow: boolean) {
  return {
    homeCountdownSegments: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: narrow ? 1 : 2,
      paddingVertical: narrow ? 3 : 4,
      paddingHorizontal: narrow ? 4 : 6,
      borderRadius: narrow ? 6 : 8,
      backgroundColor: theme.gray[200],
      borderWidth: 1,
      borderColor: theme.gray[300],
      ...(Platform.OS === 'web'
        ? makeBoxShadow({
            shadowColor: theme.gray[900],
            shadowOffset: { width: 0, height: 1 },
            shadowOpacity: 0.12,
            shadowRadius: 2,
          })
        : {
            shadowColor: theme.gray[900],
            shadowOffset: { width: 0, height: 1 },
            shadowOpacity: 0.1,
            shadowRadius: 2,
            elevation: 2,
          }),
    },
    homeCountdownSegmentsUrgent: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: narrow ? 1 : 2,
      paddingVertical: narrow ? 3 : 4,
      paddingHorizontal: narrow ? 4 : 6,
      borderRadius: narrow ? 6 : 8,
      backgroundColor: theme.gray[200],
      borderWidth: 1,
      borderColor: `${theme.error}99`,
      ...(Platform.OS === 'web'
        ? makeBoxShadow({
            shadowColor: theme.error,
            shadowOffset: { width: 0, height: 0 },
            shadowOpacity: 0.12,
            shadowRadius: 4,
          })
        : {
            shadowColor: theme.error,
            shadowOffset: { width: 0, height: 0 },
            shadowOpacity: 0.1,
            shadowRadius: 4,
            elevation: 2,
          }),
    },
    homeCountdownSegment: {
      alignItems: 'center' as const,
      justifyContent: 'center' as const,
      minWidth: narrow ? 20 : 24,
    },
    homeCountdownSegmentNumber: {
      fontSize: narrow ? 11 : 14,
      fontWeight: '700',
      letterSpacing: 0.5,
      color: theme.gray[800],
      fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
    },
    homeCountdownSegmentNumberUrgent: {
      fontSize: narrow ? 11 : 14,
      fontWeight: '700',
      letterSpacing: 0.5,
      color: theme.error,
      fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
    },
    homeCountdownSegmentLabel: {
      fontSize: narrow ? 7 : 8,
      marginTop: 1,
      color: theme.gray[600],
      textTransform: 'lowercase' as const,
    },
    homeCountdownColon: {
      fontSize: narrow ? 10 : 12,
      fontWeight: '700',
      color: theme.gray[600],
      fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
      alignSelf: 'flex-end',
      marginBottom: narrow ? 6 : 9,
    },
    homeCoinPotChip: {
      flexDirection: 'row',
      alignItems: 'center',
      alignSelf: 'center',
      paddingVertical: 6,
      paddingLeft: 10,
      paddingRight: 8,
      borderRadius: 12,
      backgroundColor: theme.accentDark + '18',
      borderWidth: 1.5,
      borderColor: theme.accentDark + '45',
      gap: 8,
    },
    homeCoinPotLabel: {
      fontSize: 10,
      fontWeight: '700',
      color: theme.gray[600],
      textTransform: 'uppercase' as const,
      letterSpacing: 0.5,
    },
    homeCoinPotInner: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
    },
    homeCoinPotText: {
      fontSize: 15,
      fontWeight: '800',
      color: theme.accentDark,
      letterSpacing: 0.3,
    },
    homeCoinIcon: {
      width: 18,
      height: 18,
    },
    homeCoinOutcomeChip: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      paddingVertical: 6,
      paddingHorizontal: 10,
      borderRadius: 12,
      borderWidth: 1.5,
    },
    homeCoinOutcomeChipWin: {
      backgroundColor: theme.success + '15',
      borderColor: theme.success + '40',
    },
    homeCoinOutcomeChipLoss: {
      backgroundColor: theme.error + '12',
      borderColor: theme.error + '35',
    },
    homeCoinOutcomeChipDraw: {
      backgroundColor: theme.gray[200],
      borderColor: theme.gray[300],
    },
    homeCoinOutcomeText: {
      fontSize: 14,
      fontWeight: '800',
      letterSpacing: 0.3,
    },
    homeCoinOutcomeTextWin: {
      color: theme.success,
    },
    homeCoinOutcomeTextLoss: {
      color: theme.error,
    },
    homeCoinOutcomeTextDraw: {
      color: theme.gray[500],
    },
    homeCountdownClock: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: narrow ? 40 : 52,
      paddingHorizontal: narrow ? 6 : 10,
      paddingVertical: narrow ? 4 : 6,
      borderRadius: narrow ? 6 : 8,
      backgroundColor: theme.gray[200],
      borderWidth: 1,
      borderColor: theme.gray[300],
    },
    homeCountdownClockText: {
      fontSize: narrow ? 10 : 12,
      fontWeight: '700',
      letterSpacing: 0.5,
      color: theme.gray[600],
      fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
    },
  };
}
